/**
 * Sticky Apartmentspalte einer Belegungszeile (Board „Belegung“): Name als Link, daneben ein Chip
 * für „Pausiert“ oder „Doppelt belegt“, darunter Adresse oder die Stellung im Wohnungsverbund,
 * rechts die Auslastung des Fensters. Ein Zimmer steht eingerückt unter seiner Gesamtwohnung, mit
 * einer feinen Führungslinie — auch beim horizontalen Scrollen.
 */
import { Link } from '@tanstack/react-router';
import type { CalendarPropertyRow } from '@/shared/api';
import { cn } from '@/shared/lib/cn';
import { flatFamilyLabel } from './calendarFamily';

interface Props {
  row: CalendarPropertyRow;
  address?: string;
  occupancyPct: number;
  paused: boolean;
  isRoom: boolean;
  doubleBooked: boolean;
  /** Schmale Handy-Spalte: Name und eine Kurzzeile, keine Auslastung. */
  compact?: boolean;
}

/** Kurzzeile der schmalen Handy-Spalte (Board „Belegung · Handy“). */
function compactSub(doubleBooked: boolean, paused: boolean, role?: string): string {
  if (doubleBooked) return 'doppelt';
  if (paused) return 'pausiert';
  return role === 'WholeFlat' ? 'gesamt' : role === 'Room' ? 'Zimmer' : '';
}

const chip = 'rounded-pill inline-flex h-5 flex-none items-center px-[7px] text-[10px] font-bold';

export function CalendarRowSidebar(props: Props) {
  const { row, paused, isRoom, doubleBooked, compact = false } = props;
  const family = flatFamilyLabel(row);
  const sub = family ?? props.address ?? '';
  return (
    <div
      className={cn(
        'bg-card sticky left-0 z-30 flex h-full min-w-0 flex-col justify-center gap-px',
        compact ? 'pl-3 pr-1' : 'pr-14',
        !compact && (isRoom ? 'pl-10' : 'pl-[18px]'),
        compact && isRoom && 'pl-5',
      )}
    >
      {isRoom && (
        <span
          aria-hidden
          className={cn('bg-line absolute inset-y-0 w-px', compact ? 'left-2.5' : 'left-[22px]')}
        />
      )}
      <span className="flex min-w-0 items-center gap-1.5">
        <Link
          to="/properties/$id"
          params={{ id: row.propertyId }}
          title={row.propertyName}
          className="text-ink hover:text-forest min-w-0 truncate text-[13px] font-bold md:text-sm"
        >
          {row.propertyName}
        </Link>
        {!compact && doubleBooked && (
          <span className={cn(chip, 'bg-sealwax-tint text-sealwax')}>Doppelt belegt</span>
        )}
        {!compact && paused && (
          <span className={cn(chip, 'bg-card-alt text-ink-soft')} title="Nimmt keine Buchungen an">
            Pausiert<span className="sr-only"> – nimmt keine Buchungen an</span>
          </span>
        )}
      </span>
      <span className="text-ink-soft truncate text-[10px] md:text-xs">
        {compact ? compactSub(doubleBooked, paused, row.flatFamily?.role) : sub}
      </span>
      {!compact && (
        <span
          className="text-ink-soft absolute right-3.5 top-1/2 -translate-y-1/2 font-mono text-xs"
          title={`${props.occupancyPct} % belegt`}
        >
          {props.occupancyPct} %
        </span>
      )}
    </div>
  );
}
